import { useEffect, useState, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import {
  Pool,
  PoolTeam,
  WorldCupTeamPick,
  WorldCupPick6EntryPicks,
  WORLD_CUP_PICK6_TEAM_COUNT,
  WORLD_CUP_PICK6_ROUND_MULTIPLIERS,
} from '../types/pool';
import { CountryFlag } from '../components/CountryFlag';
import { WorldCupTeamPicker, WorldCupView } from '../components/WorldCupTeamPicker';
import FeeNotice from '../components/FeeNotice';

const VIEW_STORAGE_KEY = 'wc-picker-view';

/**
 * World Cup Pick 6 Entry Form
 *
 * Mirrors Pick6EntryForm but groups teams by FIFA seed tier (1–12) and
 * renders country flags instead of region color blocks.
 */
export function WorldCupPick6EntryForm() {
  const { poolId, entryId } = useParams<{ poolId: string; entryId?: string }>();
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [pool, setPool] = useState<Pool | null>(null);
  const [teams, setTeams] = useState<PoolTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [entryName, setEntryName] = useState('');
  const [selectedTeams, setSelectedTeams] = useState<WorldCupTeamPick[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);

  // View tab — Seeds / Groups / Bracket. Default = Groups (most World-Cup-native lens).
  // Persist last-used choice in localStorage so it sticks across sessions.
  const [view, setView] = useState<WorldCupView>(() => {
    if (typeof window === 'undefined') return 'groups';
    const saved = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return saved === 'seeds' || saved === 'bracket' ? saved : 'groups';
  });
  function changeView(next: WorldCupView) {
    setView(next);
    try { window.localStorage.setItem(VIEW_STORAGE_KEY, next); } catch {}
  }

  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: window.location.pathname } });
      return;
    }

    async function loadData() {
      if (!poolId) return;
      try {
        setLoading(true);
        setError(null);

        const poolData = await api.getPool(poolId);
        setPool(poolData);
        setTeams(poolData.teams || []);

        if (entryId && isAuthenticated) {
          setIsEditMode(true);
          const token = await getAccessTokenSilently();
          const myEntries = await api.getMyEntries(poolId, token);
          const existingEntry = myEntries.find((e) => e.id === entryId);
          if (existingEntry) {
            setEntryName(existingEntry.entryName || '');
            const picks = existingEntry.picks as WorldCupPick6EntryPicks;
            if (picks?.teams) setSelectedTeams(picks.teams);
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load pool data');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [poolId, entryId, isAuthenticated, getAccessTokenSilently]);

  // Tier colors for the "Selected Teams Summary" chips. The grid itself uses
  // WorldCupTeamPicker which has its own tier styling.
  const seedColors: Record<string, { bg: string; border: string; text: string; chip: string }> = {
    high: { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-900', chip: 'bg-emerald-600' },
    mid:  { bg: 'bg-sky-50',     border: 'border-sky-300',     text: 'text-sky-900',     chip: 'bg-sky-600' },
    low:  { bg: 'bg-rose-50',    border: 'border-rose-300',    text: 'text-rose-900',    chip: 'bg-rose-600' },
  };
  function tierColors(seed: number) {
    if (seed <= 4) return seedColors.high;
    if (seed <= 8) return seedColors.mid;
    return seedColors.low;
  }

  function isTeamSelected(teamId: string) {
    return selectedTeams.some((t) => t.teamId === teamId);
  }

  function toggleTeam(team: PoolTeam) {
    const id = team.team.id;
    if (isTeamSelected(id)) {
      setSelectedTeams((prev) => prev.filter((t) => t.teamId !== id));
    } else {
      if (selectedTeams.length < WORLD_CUP_PICK6_TEAM_COUNT) {
        setSelectedTeams((prev) => [
          ...prev,
          {
            teamId: team.team.id,
            teamName: team.team.name,
            shortName: team.team.shortName,
            seed: team.seed ?? 12,
            isoCode: (team.metadata?.isoCode as string) ?? '',
          },
        ]);
      }
    }
  }

  // Max possible points if a team wins the tournament
  const potentialPoints = useMemo(() => {
    const maxMultiplier = Object.values(WORLD_CUP_PICK6_ROUND_MULTIPLIERS).reduce((a, b) => a + b, 0);
    return selectedTeams.reduce((total, t) => total + t.seed * maxMultiplier, 0);
  }, [selectedTeams]);

  // Group-stage points if every team advances
  const groupStagePoints = useMemo(() => {
    const groupMult = WORLD_CUP_PICK6_ROUND_MULTIPLIERS['Group Stage'];
    return selectedTeams.reduce((total, t) => total + t.seed * groupMult, 0);
  }, [selectedTeams]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (pool && new Date(pool.deadline) < new Date()) {
      setError('The deadline for this pool has passed. You can no longer submit entries.');
      return;
    }

    if (selectedTeams.length !== WORLD_CUP_PICK6_TEAM_COUNT) {
      setError(`Please select exactly ${WORLD_CUP_PICK6_TEAM_COUNT} teams`);
      return;
    }

    const hasEntryFee = pool?.entryFee && Number(pool.entryFee) > 0;

    try {
      setSubmitting(true);
      setError(null);
      const token = await getAccessTokenSilently();

      const picks: WorldCupPick6EntryPicks = {
        type: 'WORLD_CUP_BRACKET_PREDICTION',
        teams: selectedTeams,
      };

      const entryData: any = { entryName, picks };

      if (isEditMode && entryId) {
        await api.updateEntry(poolId!, entryId, entryData, token);
        navigate(`/pools/${poolId}`);
      } else {
        await api.createEntry(poolId!, entryData, token);
        if (hasEntryFee) {
          // Payment v2: entry is saved — take them to Settle Up to pay
          navigate('/settle-up', {
            state: { justSubmitted: true, submittedPool: pool?.name },
          });
        } else {
          navigate(`/pools/${poolId}`);
        }
      }
    } catch (err: any) {
      console.error('Failed to submit entry:', err);
      let errorMessage: string;
      if (
        err?.message?.includes('login_required') ||
        err?.message?.includes('Missing Refresh Token') ||
        err?.error === 'login_required' ||
        err?.message?.includes('Login required')
      ) {
        errorMessage = 'Your session has expired. Please log in again to submit your entry.';
        setTimeout(() => {
          loginWithRedirect({ appState: { returnTo: window.location.pathname } });
        }, 2000);
      } else {
        errorMessage = err instanceof Error ? err.message : 'Failed to submit entry';
      }
      setError(errorMessage);
      setTimeout(() => {
        document.querySelector('[role="alert"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !pool) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-medium">Error</p>
          <p className="text-sm mt-1">{error}</p>
          <Link to="/pools" className="mt-3 inline-block text-sm text-blue-600 hover:underline">
            ← Back to Pools
          </Link>
        </div>
      </div>
    );
  }

  if (!pool) return null;

  const isDeadlinePassed = new Date(pool.deadline) < new Date();

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link to={`/pools/${poolId}`} className="text-blue-600 hover:underline text-sm">
          ← Back to {pool.name}
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-2">
          {isEditMode ? 'Edit Your World Cup Pick 6' : 'Create Your World Cup Pick 6'}
        </h1>
        <p className="text-gray-600 mt-1">
          Pick 6 of the 48 nations. Higher seed tiers (lower-ranked teams) score more points if they advance.
        </p>
      </div>

      {/* Deadline Warning */}
      {isDeadlinePassed && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-medium">⚠️ Entry Deadline Passed</p>
          <p className="text-sm">
            The deadline for this pool was {new Date(pool.deadline).toLocaleString()}. You cannot submit or edit
            entries.
          </p>
        </div>
      )}

      {/* Scoring Info */}
      <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <h3 className="font-semibold text-emerald-900 mb-2">⚽ Scoring: Seed × Round Factor</h3>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-sm">
          {Object.entries(WORLD_CUP_PICK6_ROUND_MULTIPLIERS).map(([round, multiplier]) => (
            <div key={round} className="text-center">
              <div className="font-medium text-emerald-800">×{multiplier}</div>
              <div className="text-emerald-600 text-xs">{round}</div>
            </div>
          ))}
        </div>
        <p className="text-sm text-emerald-700 mt-2">
          Example: A 12-seed reaching the Round of 32 = 12 × 5 = 60 points
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Entry Name */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Entry Name (optional)</label>
          <input
            type="text"
            value={entryName}
            onChange={(e) => setEntryName(e.target.value)}
            placeholder="My Long-Shot Lineup"
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            disabled={isDeadlinePassed}
          />
        </div>

        {/* Selection Counter */}
        <div className="mb-4 flex items-center justify-between">
          <div className="text-lg font-semibold">
            Selected:{' '}
            <span className={selectedTeams.length === WORLD_CUP_PICK6_TEAM_COUNT ? 'text-green-600' : 'text-emerald-700'}>
              {selectedTeams.length}/{WORLD_CUP_PICK6_TEAM_COUNT}
            </span>
          </div>
          <div className="text-sm text-gray-600">
            Group-Stage Points: <span className="font-medium">{groupStagePoints}</span>
            {' • '}
            Max Potential: <span className="font-medium">{potentialPoints}</span>
          </div>
        </div>

        {/* Selected Teams Summary */}
        {selectedTeams.length > 0 && (
          <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Your Picks</h3>
            <div className="flex flex-wrap gap-2">
              {selectedTeams.map((team) => {
                const c = tierColors(team.seed);
                return (
                  <div
                    key={team.teamId}
                    className={`pl-2 pr-3 py-2 rounded-lg ${c.bg} border ${c.border} flex items-center gap-2`}
                  >
                    <CountryFlag iso={team.isoCode} alt={team.teamName} size="sm" rounded />
                    <span className="text-xs font-medium text-gray-500">Seed {team.seed}</span>
                    <span className={`font-medium ${c.text}`}>{team.teamName}</span>
                    <button
                      type="button"
                      onClick={() => setSelectedTeams((prev) => prev.filter((t) => t.teamId !== team.teamId))}
                      className="ml-1 text-gray-400 hover:text-red-500"
                      disabled={isDeadlinePassed}
                      aria-label={`Remove ${team.teamName}`}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Team Selection — three views (Seeds / Groups / Bracket) */}
        {teams.length === 0 ? (
          <div className="mb-8 bg-amber-50 border-2 border-amber-300 rounded-lg p-6 text-center">
            <span className="text-3xl block mb-2">⚽</span>
            <h3 className="font-semibold text-gray-900 mb-2">World Cup Field Not Yet Available</h3>
            <p className="text-gray-600">
              Teams will appear here once the 48-nation field has been imported by an admin.
            </p>
          </div>
        ) : (
          <div className="mb-8">
            <WorldCupTeamPicker
              teams={teams}
              selectedTeamIds={new Set(selectedTeams.map((t) => t.teamId))}
              maxSelections={WORLD_CUP_PICK6_TEAM_COUNT}
              onToggle={toggleTeam}
              readOnly={isDeadlinePassed}
              view={view}
              onViewChange={changeView}
              helpText="Pick exactly 6 teams. Switch tabs to view by FIFA seed, by World Cup group, or by bracket position."
            />
          </div>
        )}

        {/* Entry fee notice — payment happens after submit (Settle Up) */}
        {pool && pool.entryFee && Number(pool.entryFee) > 0 && !isEditMode && (
          <div className="mb-6">
            <FeeNotice entryFee={Number(pool.entryFee)} />
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 border-2 border-red-300 rounded-lg p-4 text-red-800 font-medium" role="alert">
            {error}
          </div>
        )}

        <div className="flex gap-4">
          <button
            type="submit"
            disabled={
              submitting ||
              isDeadlinePassed ||
              selectedTeams.length !== WORLD_CUP_PICK6_TEAM_COUNT
            }
            className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {submitting
              ? 'Submitting...'
              : isEditMode
              ? 'Update Entry'
              : 'Submit Entry'}
          </button>
          <Link
            to={`/pools/${poolId}`}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Cancel
          </Link>
        </div>
      </form>

      <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="font-semibold text-yellow-800 mb-2">💡 Strategy Tips</h3>
        <ul className="text-sm text-yellow-800 space-y-1">
          <li>• <strong>Seed 1–4 (top FIFA tiers)</strong> are safer but score fewer points per round</li>
          <li>• <strong>Seed 5–8 (mid)</strong> balance risk and reward — solid quarterfinal threats</li>
          <li>• <strong>Seed 9–12 (long shots)</strong> are risky but hugely valuable if they advance</li>
          <li>• A Seed 12 team that just clears the group stage scores 48 pts — more than a Seed 1 winning the title (49)!</li>
        </ul>
      </div>
    </div>
  );
}

export default WorldCupPick6EntryForm;
